/**
 * Region derivation from the branch tree.
 *
 * A branch's region is the root-level group it descends from. There is no
 * region column: the parent chain already answers this, and a stored copy
 * would drift the moment a director reparents a node.
 */

export type TreeUnit = {
  code: string;
  name: string;
  shortLabel?: string;
  kind: "group" | "branch";
  parentCode?: string;
  isActive?: boolean;
  sortOrder?: number;
  // Optional because the offline fallback list carries no flags. Absent reads
  // as "off", which is the safe default: it offers no module that is not
  // actually switched on at that site.
  shiftOpsEnabled?: boolean;
  // Số ekip chạy song song tại cơ sở. Optional vì danh sách fallback offline
  // không có — nơi dùng phải chịu được absent (VD form đóng ca chỉ cho "đóng
  // toàn bộ" khi không biết sức chứa) thay vì đoán một con số.
  capacity?: number;
  // Số kíp riêng của từng thương hiệu (mã chữ thường → số kíp, QĐ 0523) — trần
  // bên trong `capacity`. Optional cùng lý do: danh sách offline không có, và
  // thương hiệu vắng mặt chỉ theo `capacity`.
  ekipByBrand?: Record<string, number>;
  // Số lễ tân tối đa cùng một ca tại cơ sở. KHÁC `capacity` ngay trên: cái đó
  // đếm buổi chụp song song, cái này đếm người đứng quầy — mở thêm phòng chụp
  // là một số lên còn số kia đứng yên. Optional cùng lý do: danh sách fallback
  // offline không mang cấu hình, nơi dùng phải rơi về mặc định thay vì đoán.
  letanMaxStaffPerShift?: number;
};

export type Region = { id: string; label: string };

export const ALL_REGION: Region = { id: "all", label: "Toàn quốc" };

/** Walks to the root-level group. Cycle-safe: a corrupt parent chain must not hang the UI. */
export function rootGroupOf(code: string, units: readonly TreeUnit[]): string | null {
  const byCode = new Map(units.map((unit) => [unit.code, unit]));
  const seen = new Set<string>();
  let current = byCode.get(code);
  if (!current) return null;

  while (current.parentCode) {
    if (seen.has(current.code)) return null;
    seen.add(current.code);
    const parent = byCode.get(current.parentCode);
    if (!parent) break;
    current = parent;
  }
  return current.code === code && current.kind !== "group" ? null : current.code;
}

/**
 * Regions derived from root-level groups, "Toàn quốc" always first.
 *
 * Deriving rather than hardcoding is what makes a region a director creates
 * appear as a tab without a code change.
 */
export function regionsFromUnits(units: readonly TreeUnit[]): Region[] {
  const roots = units
    .filter((unit) => unit.kind === "group" && !unit.parentCode)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.code.localeCompare(b.code))
    .map((unit) => ({ id: unit.code, label: unit.name }));
  return [ALL_REGION, ...roots];
}

/**
 * Maps a branch value to its region key.
 *
 * Handles the synthetic `<group>_all` values stored in `staff.branch`, then the
 * tree, then a prefix guess for free-text legacy values.
 */
export function regionOfBranchIn(
  branch: string | null | undefined,
  units: readonly TreeUnit[],
): string {
  if (!branch) return "all";
  const value = branch.toLowerCase();
  if (value === "all") return "all";

  // "<group>_all" is a region scope, not a branch.
  const allSuffix = value.endsWith("_all") ? value.slice(0, -"_all".length) : null;
  if (allSuffix) {
    const group = units.find((unit) => unit.code === allSuffix && unit.kind === "group");
    if (group) return rootGroupOf(group.code, units) ?? group.code;
    return allSuffix;
  }

  const root = rootGroupOf(value, units);
  if (root) return root;

  // Legacy free-text branch names never in the catalog. Kept so historical
  // rows still land in a sensible tab rather than silently in "Toàn quốc".
  if (value.startsWith("hn")) return "hn";
  if (value.startsWith("sg") || value.startsWith("hcm")) return "sg";
  return "all";
}
